import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader, CheckCircle, Building2, ChevronRight, AlertTriangle, AlertOctagon, ArrowUpRight, Mail } from 'lucide-react';
import sponsorService from '../../services/sponsorService';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';

const SponsorSelector = () => {
    const [sponsors, setSponsors] = useState([]);             // sponsors user is assigned to
    const [allSponsors, setAllSponsors] = useState([]);       // all sponsors in system (superuser only)

    const [loading, setLoading] = useState(true);
    const [selectedSponsor, setSelectedSponsor] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, user, userPermissions } = useContext(AuthContext);
    const [hasPermission, setHasPermission] = useState(null);
    const [accessDenied, setAccessDenied] = useState(false);

    const [showBackButton, setShowBackButton] = useState(false);

    // Superuser sponsor management state
    const isSuperuser = user?.is_superuser;
    const [linkedSponsorIds, setLinkedSponsorIds] = useState([]);
    const [updatingSponsorId, setUpdatingSponsorId] = useState(null);

    // Superuser sponsor creation state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({
        sponsor_id: '',
        sponsor_name: '',
        sponsor_email: '',
        sponsor_phone_number: '',
        sponsor_contact_first_name: '',
        sponsor_contact_last_name: '',
        email_domains: ''
    });
    const [creatingSponsor, setCreatingSponsor] = useState(false);
    const [createError, setCreateError] = useState(null);

    useEffect(() => {
        const fromMainMenu = location.state?.from === 'mainMenu';
        setShowBackButton(fromMainMenu);
    }, [location]);

    // Check sponsor selection permission using bulk permissions from AuthContext.
    // This avoids multiple /api/user/permissions/<perm>/ calls which can trigger rate limiting.
    useEffect(() => {
        if (!isAuthenticated) {
            setHasPermission(false);
            setAccessDenied(true);
            setLoading(false);
            // Redirect to login after a short delay to allow seeing the access denied message
            setTimeout(() => navigate('/login', { state: { from: location.pathname } }), 3000);
            return;
        }

        // userPermissions may load slightly after isAuthenticated flips true.
        // Wait for permissions to actually load (not just an empty object)
        if (!userPermissions || Object.keys(userPermissions).length === 0) {
            setHasPermission(null);
            return;
        }

        const canSelectSponsor = userPermissions.can_select_sponsor === true;
        setHasPermission(canSelectSponsor);

        if (!canSelectSponsor) {
            setAccessDenied(true);
            setLoading(false);
            // Redirect to home/dashboard after a short delay
            setTimeout(() => navigate('/'), 3000);
        }
    }, [isAuthenticated, navigate, location.pathname, userPermissions]);

    useEffect(() => {
        const fetchSponsors = async () => {
            if (!hasPermission) {
                return;
            }

            try {
                const response = await sponsorService.getSponsors();
                
                // Handle auto-bypass response for single-sponsor users
                if (response.auto_selected) {
                    console.log('Single sponsor auto-selected, redirecting to studies');
                    // Update auth context if tokens are provided
                    if (response.tokens?.access_token) {
                        localStorage.setItem('authToken', response.tokens.access_token);
                        if (response.tokens.refresh_token) {
                            localStorage.setItem('refreshToken', response.tokens.refresh_token);
                        }
                    }
                    
                    // Store selected sponsor info
                    if (response.sponsor_id && response.sponsor_name) {
                        localStorage.setItem('selectedSponsor', JSON.stringify({
                            id: response.sponsor_id,
                            name: response.sponsor_name
                        }));
                        window.dispatchEvent(new Event('storage'));
                    }

                    // Redirect to studies
                    navigate('/studies');
                    return;
                }
                
                // Normal case - set sponsors list (only sponsors this user is assigned to)
                setSponsors(response);

                // For superusers, also:
                // 1) Load which sponsors are currently linked via M2M (for badges/buttons)
                // 2) Load ALL sponsors in the system for the management panel
                if (isSuperuser) {
                    try {
                        const links = await sponsorService.getMySponsorLinks();
                        if (Array.isArray(links?.sponsor_ids)) {
                            setLinkedSponsorIds(links.sponsor_ids);
                        }
                    } catch (linkError) {
                        console.error('Error loading current sponsor links:', linkError);
                    }

                    try {
                        const all = await sponsorService.getAllSponsors();
                        setAllSponsors(Array.isArray(all) ? all : []);
                    } catch (allError) {
                        console.error('Error loading all sponsors for superuser panel:', allError);
                    }
                }
            } catch (error) {
                if (error?.response?.status === 429) {
                    // If rate limited, wait 2 seconds and try again
                    setTimeout(async () => {
                        try {
                            const response = await sponsorService.getSponsors();
                            
                            // Handle auto-bypass on retry too
                            if (response.auto_selected) {
                                console.log('Single sponsor auto-selected on retry, redirecting to studies');
                                if (response.tokens?.access_token) {
                                    localStorage.setItem('authToken', response.tokens.access_token);
                                    if (response.tokens.refresh_token) {
                                        localStorage.setItem('refreshToken', response.tokens.refresh_token);
                                    }
                                }
                                if (response.sponsor_id && response.sponsor_name) {
                                    localStorage.setItem('selectedSponsor', JSON.stringify({
                                        id: response.sponsor_id,
                                        name: response.sponsor_name
                                    }));
                                    window.dispatchEvent(new Event('storage'));
                                }
                                navigate('/studies');
                                return;
                            }
                            
                            setSponsors(response);
                        } catch (retryError) {
                            console.error('Error retrying sponsor fetch:', retryError);
                        }
                    }, 2000);
                } else {
                    console.error('Error fetching sponsors:', error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchSponsors();
    }, [hasPermission, navigate, isSuperuser]);

    const handleToggleSponsorLink = async (sponsor) => {
        if (!isSuperuser) return;
        const sponsorId = sponsor.sponsor_id;
        const currentlyLinked = linkedSponsorIds.includes(sponsorId);
        setUpdatingSponsorId(sponsorId);
        try {
            if (currentlyLinked) {
                await sponsorService.removeSponsorFromUser(sponsorId);
                setLinkedSponsorIds((prev) => prev.filter((id) => id !== sponsorId));
                // Optimistically remove from current assigned sponsors list so UI updates immediately
                setSponsors((prev) => prev.filter((s) => s.sponsor_id !== sponsorId));
            } else {
                await sponsorService.addSponsorToUser(sponsorId);
                setLinkedSponsorIds((prev) => prev.includes(sponsorId) ? prev : [...prev, sponsorId]);
            }

            // After changing links, refresh the assigned sponsors list so the
            // main selector grid reflects the new associations immediately.
            try {
                const refreshed = await sponsorService.getSponsors();
                if (!refreshed.auto_selected) {
                    setSponsors(refreshed);
                }
            } catch (refreshError) {
                console.error('Error refreshing assigned sponsors after link change:', refreshError);
            }
        } catch (error) {
            console.error('Error toggling sponsor link:', error);
        } finally {
            setUpdatingSponsorId(null);
        }
    };

    const handleSponsorSelect = async (sponsor) => {
        setSelectedSponsor(sponsor);
        try {
            console.log('Selecting sponsor:', sponsor.sponsor_id);
            const response = await sponsorService.selectSponsor(sponsor.sponsor_id);
            console.log('Sponsor selection response:', response);

            // Store selected sponsor with full details
            localStorage.setItem('selectedSponsor', JSON.stringify({
                id: sponsor.sponsor_id,
                name: sponsor.sponsor_name
            }));
            window.dispatchEvent(new Event('storage'));

            // Navigate directly to studies
            navigate('/studies');
        } catch (error) {
            console.error('Error selecting sponsor:', error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
            }
            setSelectedSponsor(null);
        }
    };

    const handleCreateSponsor = async () => {
        setCreatingSponsor(true);
        setCreateError(null);
        try {
            // Convert comma-separated email_domains string into a list for backend
            const emailDomainsList = (createForm.email_domains || '')
                .split(',')
                .map((d) => d.trim())
                .filter((d) => d.length > 0);

            const payload = {
                ...createForm,
                sponsor_is_active: true,
                sponsor_active_users: 0,
                email_domains: emailDomainsList,
            };
            const newSponsor = await sponsorService.createSponsor(payload);
            // New sponsor should now be linked to the creating user (backend logic),
            // so add it to the assigned sponsors list
            setSponsors((prev) => [...prev, newSponsor]);

            // Also refresh the full sponsor list for the Superuser panel
            try {
                const all = await sponsorService.getAllSponsors();
                setAllSponsors(Array.isArray(all) ? all : []);
            } catch (allError) {
                console.error('Error refreshing all sponsors after create:', allError);
            }

            setShowCreateModal(false);

            setCreateForm({
                sponsor_id: '',
                sponsor_name: '',
                sponsor_email: '',
                sponsor_phone_number: '',
                sponsor_contact_first_name: '',
                sponsor_contact_last_name: '',
                email_domains: ''
            });
        } catch (error) {
            setCreateError(error?.response?.data || 'Failed to create sponsor');
        } finally {
            setCreatingSponsor(false);
        }
    };

    // Redirect users without permission
    useEffect(() => {
        if (hasPermission === false) {
            console.log('User does not have sponsor selection permission, redirecting to /studies');
            navigate('/studies', { 
                state: { 
                    accessDenied: true, 
                    message: 'You do not have permission to access the Sponsor Selection.' 
                } 
            });
        }
    }, [hasPermission, navigate]);
    
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-[3px] border-primary-600 border-t-transparent"></div>
            </div>
        );
    }
    
    if (hasPermission === false) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/30 p-8 text-center max-w-md border border-transparent dark:border-gray-700">
                    <div className="flex justify-center mb-4">
                        <div className="bg-red-100 dark:bg-red-900/20 p-3 rounded-full">
                            <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
                        </div>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">You do not have permission to access Sponsor Selection.</p>
                    <button
                        onClick={() => navigate('/studies')}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        Return to Studies
                    </button>
                </div>
            </div>
        );
    }

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            },
        },
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
        },
    };

    return (
        <>
            <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
                <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-md dark:shadow-gray-900/30 border border-gray-200/50 dark:border-gray-700">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="p-8"
                    >
                        {accessDenied ? (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="bg-red-100 dark:bg-red-900/20 rounded-full p-4 mb-6">
                                    <AlertOctagon className="w-16 h-16 text-red-500 dark:text-red-400" />
                                </div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Access Denied</h1>
                                <p className="text-gray-600 dark:text-gray-400 text-center mb-6 max-w-md">
                                    You do not have permission to access Sponsor Selection.
                                </p>
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: '100%' }}
                                    transition={{ duration: 3 }}
                                    className="h-1 bg-gray-300 dark:bg-gray-700 rounded-full w-64 mb-6 overflow-hidden"
                                >
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: '100%' }}
                                        transition={{ duration: 3 }}
                                        className="h-full bg-primary-500"
                                    />
                                </motion.div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Redirecting you...</p>
                            </div>
                        ) : (
                            <>
                                <div className="mb-8">
                                    <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Select a Sponsor</h1>
                                    <p className="text-gray-600 dark:text-gray-400">Choose a sponsor to access their studies and data.</p>
                                </div>

                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center">
                                            <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2" />
                                            <h2 className="text-xl font-medium text-gray-900 dark:text-gray-100">Available Sponsors</h2>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {isSuperuser && (
                                                <button
                                                    onClick={() => setShowCreateModal(true)}
                                                    className="px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                                                >
                                                    + Create Sponsor
                                                </button>
                                            )}
                                            {showBackButton && (
                                                <button
                                                    onClick={() => navigate('/')}
                                                    className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                                                >
                                                    Back
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {loading ? (
                                        <div className="flex justify-center items-center h-64">
                                            <Loader className="w-12 h-12 text-primary-600 animate-spin" />
                                        </div>
                                    ) : sponsors.length === 0 ? (
                                        // Empty state when no sponsors are assigned
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5 }}
                                            className="flex flex-col items-center justify-center py-16 px-8"
                                        >
                                            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-full mb-6">
                                                <AlertTriangle className="w-12 h-12 text-amber-600 dark:text-amber-400" />
                                            </div>
                                            <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4 text-center">
                                                No Sponsors Assigned
                                            </h3>
                                            <p className="text-gray-600 dark:text-gray-400 text-center mb-6 max-w-md leading-relaxed">
                                                You haven't been assigned to any sponsors yet. Please contact the dev team to request sponsor access.
                                            </p>
                                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-4 max-w-md">
                                                <div className="flex items-start">
                                                    <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full mr-3 mt-1">
                                                        <AlertOctagon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                                                            Need Access?
                                                        </h4>
                                                        <p className="text-sm text-blue-700 dark:text-blue-300">
                                                            Contact the dev team to be assigned to one or more sponsors.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            {showBackButton && (
                                                <button
                                                    onClick={() => navigate('/')}
                                                    className="mt-6 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                                                >
                                                    Back to Dashboard
                                                </button>
                                            )}
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            variants={containerVariants}
                                            initial="hidden"
                                            animate="visible"
                                            className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
                                        >
                                            <AnimatePresence>
                                                {sponsors.map((sponsor) => (
                                                    <motion.div
                                                        key={sponsor.sponsor_id}
                                                        variants={itemVariants}
                                                        whileHover={{ 
                                                            y: -4, 
                                                            transition: { duration: 0.2 } 
                                                        }}
                                                        exit={{ opacity: 0, scale: 0.5 }}
                                                        className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900/20 overflow-hidden transition-all duration-300 hover:shadow-lg dark:hover:shadow-gray-900/30 border border-gray-100 dark:border-gray-700 cursor-pointer group relative flex flex-col h-full ${
                                                            selectedSponsor?.sponsor_id === sponsor.sponsor_id ? 'ring-2 ring-primary-500' : ''
                                                        }`}
                                                        onClick={() => handleSponsorSelect(sponsor)}
                                                    >
                                                        <div className="p-6 flex-1 flex flex-col">
                                                            <div className="flex items-center justify-between mb-4">
                                                                <div className="bg-primary-50 dark:bg-primary-900/20 p-3 rounded-full">
                                                                    <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                                                                </div>
                                                                {selectedSponsor?.sponsor_id === sponsor.sponsor_id && (
                                                                    <CheckCircle className="w-5 h-5 text-primary-500" />
                                                                )}
                                                            </div>
                                                            
                                                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
                                                                {sponsor.sponsor_name}
                                                            </h2>
                                                            
                                                            <div className="mt-auto pt-4 space-y-2">
                                                                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                                                    <Mail className="w-3.5 h-3.5 mr-1.5" />
                                                                    <span>{sponsor.sponsor_email}</span>
                                                                </div>
                                                                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                                                    <span>ID: {sponsor.sponsor_id}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="mt-auto px-6 py-3 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
                                                            <span className="text-sm font-medium text-primary-600 dark:text-primary-400">View details</span>
                                                            <ArrowUpRight className="w-4 h-4 text-primary-600 dark:text-primary-400 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        </motion.div>
                                    )}
                                </div>
                            </>
                        )}
                    </motion.div>
                </div>
            </div>
            {isSuperuser && allSponsors.length > 0 && (
                <div className="max-w-5xl mx-auto mt-4 mb-12 px-4">

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Superuser Sponsor Access</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Quickly add or remove sponsors from your account. This controls which sponsors you can switch into from the selector.
                        </p>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {allSponsors.map((sponsor) => {

                                const linked = linkedSponsorIds.includes(sponsor.sponsor_id);
                                const isUpdating = updatingSponsorId === sponsor.sponsor_id;
                                return (
                                    <div
                                        key={`manage-${sponsor.sponsor_id}`}
                                        className="border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 flex flex-col justify-between bg-gray-50 dark:bg-gray-700/30"
                                    >
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                                    {sponsor.sponsor_name}
                                                </span>
                                                {linked && (
                                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                                                        Linked
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">ID: {sponsor.sponsor_id}</p>
                                        </div>
                                        <div className="mt-3 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => handleToggleSponsorLink(sponsor)}
                                                disabled={isUpdating}
                                                className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                                    linked
                                                        ? 'border-rose-300 dark:border-rose-700/40 text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 hover:bg-rose-100 dark:hover:bg-rose-900/30'
                                                        : 'border-primary-300 dark:border-primary-700/40 text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30'
                                                } ${isUpdating ? 'opacity-60 cursor-wait' : ''}`}
                                            >
                                                {isUpdating
                                                    ? 'Updating...'
                                                    : linked
                                                        ? 'Remove from my sponsors'
                                                        : 'Add to my sponsors'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
            {isSuperuser && showCreateModal && (
                <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-overlay dark:shadow-gray-900/30 w-full max-w-lg p-6 animate-fade-in border border-transparent dark:border-gray-700">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Create Sponsor</h2>
                        {createError && (
                            <div className="mb-4 text-sm text-red-600">
                                {typeof createError === 'string' ? createError : JSON.stringify(createError)}
                            </div>
                        )}
                        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sponsor ID</label>
                                <input
                                    type="text"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    value={createForm.sponsor_id}
                                    onChange={(e) => setCreateForm({ ...createForm, sponsor_id: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                                <input
                                    type="text"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    value={createForm.sponsor_name}
                                    onChange={(e) => setCreateForm({ ...createForm, sponsor_name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                                <input
                                    type="email"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    value={createForm.sponsor_email}
                                    onChange={(e) => setCreateForm({ ...createForm, sponsor_email: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                                <input
                                    type="text"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    value={createForm.sponsor_phone_number}
                                    onChange={(e) => setCreateForm({ ...createForm, sponsor_phone_number: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact First Name</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                        value={createForm.sponsor_contact_first_name}
                                        onChange={(e) => setCreateForm({ ...createForm, sponsor_contact_first_name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Last Name</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                        value={createForm.sponsor_contact_last_name}
                                        onChange={(e) => setCreateForm({ ...createForm, sponsor_contact_last_name: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Domains (comma-separated)</label>
                                <input
                                    type="text"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                    value={createForm.email_domains}
                                    onChange={(e) => setCreateForm({ ...createForm, email_domains: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setCreateError(null);
                                }}
                                disabled={creatingSponsor}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                                onClick={handleCreateSponsor}
                                disabled={creatingSponsor}
                            >
                                {creatingSponsor ? 'Creating...' : 'Create Sponsor'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SponsorSelector;